import React, { useState, useCallback, useEffect } from 'react';
import {
  Container, Typography, Box, Button, CircularProgress, Alert, Paper, Grid, TextField, 
  Dialog, DialogActions, DialogContent, DialogTitle, Switch, FormControlLabel,
  TableContainer, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Chip,
  Checkbox, FormGroup, FormLabel, Divider
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { supabase } from './supabaseClient';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';

// Helper to safely convert form values to numbers or null
const safeParseInt = (value) => (value === '' || value === null || value === undefined) ? null : parseInt(value, 10);
const safeParseFloat = (value) => (value === '' || value === null || value === undefined) ? null : parseFloat(value);

const PackagesAdmin = () => {
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [packagesError, setPackagesError] = useState(null);

  // NEW: State for LLM models
  const [availableModels, setAvailableModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [selectedModels, setSelectedModels] = useState([]);

  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [isCreatingPackage, setIsCreatingPackage] = useState(false);
  const [createPackageError, setCreatePackageError] = useState(null);
  
  // Add state for editing a package
  const [editingPackage, setEditingPackage] = useState(null); // Holds the package being edited
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [isUpdatingPackage, setIsUpdatingPackage] = useState(false);
  const [updatePackageError, setUpdatePackageError] = useState(null);

  // Add state for deleting a package
  const [deletingPackage, setDeletingPackage] = useState(null); // Holds the package being deleted
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isDeletingPackage, setIsDeletingPackage] = useState(false);
  const [deletePackageError, setDeletePackageError] = useState(null);
  
  // Add state for duplicating a package
  const [duplicatingPackage, setDuplicatingPackage] = useState(null);
  const [openDuplicateDialog, setOpenDuplicateDialog] = useState(false);

  const initialPackageFormState = {
    // Core Details
    name: '',
    description: '',
    price_usd: '',
    package_tier: 'basic',
    is_active: true,
    is_popular: false,
    is_enterprise: false,
    sort_order: 0,

    // Discount Settings
    discount_percentage: 0,
    discount_active: false,
    discount_reason: '',

    // Billing & Stripe
    stripe_price_id: '',
    stripe_product_id: '',
    billing_enabled: true,
    overage_allowed: false,
    price_per_extra_token: '',
    trial_period_days: 0,
    auto_upgrade_enabled: false,

    // Token & Cost Limits
    limit_monthly_tokens: '',
    daily_token_limit: '',
    per_request_token_limit: '',
    limit_monthly_cost: '',
    daily_cost_limit: '',
    per_request_cost_limit: '',
    
    // Usage Limits
    limit_monthly_messages: '',
    limit_daily_messages: '',
    hourly_message_limit: '',
    limit_monthly_sessions: '',
    limit_monthly_searches: '',
    limit_daily_searches: '',
    limit_monthly_deep_searches: '',
    limit_daily_deep_searches: '',
    concurrent_requests_limit: '',
    limit_team_members: '',
    limit_total_files: '',
    
    // Rate Limiting
    requests_per_minute: '',
    requests_per_hour: '',
    burst_limit: '',
    max_active_sessions: '',
    max_conversation_length: '',
    
    // File & Upload Limits
    max_file_size_mb: '',
    max_files_per_upload: '',
    monthly_upload_limit_mb: '',
    limit_total_upload_mb: '',
    allowed_file_types: '["pdf", "txt", "docx", "md"]',
    allowed_models: '[]', // NEW: Default to empty array
    
    // Feature Flags
    api_access_enabled: false,
    premium_models_enabled: false,
    custom_model_access: false,
    team_collaboration_enabled: false,
    chatwoot_integration_enabled: false,
    priority_support: false,
    webhook_enabled: false,
    analytics_enabled: true,
    export_data_enabled: false,
    
    // Enterprise Features
    custom_branding_enabled: false,
    white_label_enabled: false,
    sso_enabled: false,
    audit_logs_enabled: false,
    
    // Configuration
    features: '{}',
    custom_config: '{}',

    // Misc
    chat_history_retention_days: 30,
    quota_cache_ttl_seconds: 300,
  };
  const [packageForm, setPackageForm] = useState(initialPackageFormState);


  const getAuthToken = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  }, []);

  // NEW: Fetch available LLM models
  const fetchAvailableModels = useCallback(async () => {
    setLoadingModels(true);
    try {
      const { data, error } = await supabase
        .from('llm_models')
        .select('model_identifier, display_label, is_premium, is_active, provider')
        .eq('is_active', true)
        .order('provider', { ascending: true })
        .order('display_label', { ascending: true });
      
      if (error) throw error;
      setAvailableModels(data || []);
    } catch (error) {
      console.error("Error fetching LLM models:", error);
      setPackagesError(`Failed to load available models: ${error.message}`);
    } finally {
      setLoadingModels(false);
    }
  }, []);

  const handleFetchPackages = useCallback(async () => {
    setLoadingPackages(true);
    setPackagesError(null);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error("Authentication token not found. Please log in.");
      const anonKey = (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_ANON_KEY) || process.env.REACT_APP_SUPABASE_ANON_KEY;
      if (!anonKey) throw new Error("Supabase anon key is not configured.");
      
      const response = await fetch(`/functions/v1/admin-package-manager/packages`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'apikey': anonKey,
          'Content-Type': 'application/json'
        },
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || `Failed to fetch packages: ${response.status} ${result.message}`);
      }
      setPackages(result.packages || []);
    } catch (error) {
      console.error("Error fetching packages:", error);
      setPackagesError(error.message);
    } finally {
      setLoadingPackages(false);
    }
  }, [getAuthToken]);

  useEffect(() => {
    handleFetchPackages();
    fetchAvailableModels(); // NEW: Fetch models on component mount
  }, [handleFetchPackages, fetchAvailableModels]);

  const handleOpenCreateDialog = () => {
    setEditingPackage(null);
    setPackageForm(initialPackageFormState);
    setSelectedModels([]); // NEW: Reset selected models
    setCreatePackageError(null);
    setOpenCreateDialog(true);
  };
  const handleCloseCreateDialog = () => setOpenCreateDialog(false);

  const handleOpenEditDialog = (pkg) => {
    setEditingPackage(pkg);
    // Populate form with all fields from the package, providing defaults for nulls
    setPackageForm({
      name: pkg.name || '',
      description: pkg.description || '',
      price_usd: pkg.price_usd !== null ? String(pkg.price_usd) : '',
      package_tier: pkg.package_tier || 'basic',
      is_active: pkg.is_active,
      is_popular: pkg.is_popular,
      is_enterprise: pkg.is_enterprise,
      sort_order: pkg.sort_order !== null ? String(pkg.sort_order) : '0',
      
      // Discount Settings
      discount_percentage: pkg.discount_percentage !== null ? String(pkg.discount_percentage) : '0',
      discount_active: pkg.discount_active || false,
      discount_reason: pkg.discount_reason || '',
      
      stripe_price_id: pkg.stripe_price_id || '',
      stripe_product_id: pkg.stripe_product_id || '',
      billing_enabled: pkg.billing_enabled,
      overage_allowed: pkg.overage_allowed,
      price_per_extra_token: pkg.price_per_extra_token !== null ? String(pkg.price_per_extra_token) : '',
      trial_period_days: pkg.trial_period_days !== null ? String(pkg.trial_period_days) : '0',
      auto_upgrade_enabled: pkg.auto_upgrade_enabled || false,

      limit_monthly_tokens: pkg.limit_monthly_tokens !== null ? String(pkg.limit_monthly_tokens) : '',
      daily_token_limit: pkg.daily_token_limit !== null ? String(pkg.daily_token_limit) : '',
      per_request_token_limit: pkg.per_request_token_limit !== null ? String(pkg.per_request_token_limit) : '',
      limit_monthly_cost: pkg.limit_monthly_cost !== null ? String(pkg.limit_monthly_cost) : '',
      daily_cost_limit: pkg.daily_cost_limit !== null ? String(pkg.daily_cost_limit) : '',
      per_request_cost_limit: pkg.per_request_cost_limit !== null ? String(pkg.per_request_cost_limit) : '',

      limit_monthly_messages: pkg.limit_monthly_messages !== null ? String(pkg.limit_monthly_messages) : '',
      limit_daily_messages: pkg.limit_daily_messages !== null ? String(pkg.limit_daily_messages) : '',
      hourly_message_limit: pkg.hourly_message_limit !== null ? String(pkg.hourly_message_limit) : '',
      limit_monthly_sessions: pkg.limit_monthly_sessions !== null ? String(pkg.limit_monthly_sessions) : '',
      limit_monthly_searches: pkg.limit_monthly_searches !== null ? String(pkg.limit_monthly_searches) : '',
      limit_daily_searches: pkg.limit_daily_searches !== null ? String(pkg.limit_daily_searches) : '',
      limit_monthly_deep_searches: pkg.limit_monthly_deep_searches !== null ? String(pkg.limit_monthly_deep_searches) : '',
      limit_daily_deep_searches: pkg.limit_daily_deep_searches !== null ? String(pkg.limit_daily_deep_searches) : '',
      concurrent_requests_limit: pkg.concurrent_requests_limit !== null ? String(pkg.concurrent_requests_limit) : '',
      limit_team_members: pkg.limit_team_members !== null ? String(pkg.limit_team_members) : '',
      limit_total_files: pkg.limit_total_files !== null ? String(pkg.limit_total_files) : '',

      // Rate Limiting
      requests_per_minute: pkg.requests_per_minute !== null ? String(pkg.requests_per_minute) : '',
      requests_per_hour: pkg.requests_per_hour !== null ? String(pkg.requests_per_hour) : '',
      burst_limit: pkg.burst_limit !== null ? String(pkg.burst_limit) : '',
      max_active_sessions: pkg.max_active_sessions !== null ? String(pkg.max_active_sessions) : '',
      max_conversation_length: pkg.max_conversation_length !== null ? String(pkg.max_conversation_length) : '',

      // File & Upload Limits
      max_file_size_mb: pkg.max_file_size_mb !== null ? String(pkg.max_file_size_mb) : '',
      max_files_per_upload: pkg.max_files_per_upload !== null ? String(pkg.max_files_per_upload) : '',
      monthly_upload_limit_mb: pkg.monthly_upload_limit_mb !== null ? String(pkg.monthly_upload_limit_mb) : '',
      limit_total_upload_mb: pkg.limit_total_upload_mb !== null ? String(pkg.limit_total_upload_mb) : '',
      allowed_file_types: pkg.allowed_file_types ? JSON.stringify(pkg.allowed_file_types) : '["pdf", "txt", "docx", "md"]',

      // Feature Flags
      api_access_enabled: pkg.api_access_enabled || false,
      premium_models_enabled: pkg.premium_models_enabled || false,
      custom_model_access: pkg.custom_model_access || false,
      team_collaboration_enabled: pkg.team_collaboration_enabled || false,
      chatwoot_integration_enabled: pkg.chatwoot_integration_enabled || false,
      priority_support: pkg.priority_support || false,
      webhook_enabled: pkg.webhook_enabled || false,
      analytics_enabled: pkg.analytics_enabled !== undefined ? pkg.analytics_enabled : true,
      export_data_enabled: pkg.export_data_enabled || false,

      // Enterprise Features
      custom_branding_enabled: pkg.custom_branding_enabled || false,
      white_label_enabled: pkg.white_label_enabled || false,
      sso_enabled: pkg.sso_enabled || false,
      audit_logs_enabled: pkg.audit_logs_enabled || false,

      // Configuration
      features: pkg.features ? JSON.stringify(pkg.features) : '{}',
      custom_config: pkg.custom_config ? JSON.stringify(pkg.custom_config) : '{}',
      
      chat_history_retention_days: pkg.chat_history_retention_days !== null ? String(pkg.chat_history_retention_days) : '30',
      quota_cache_ttl_seconds: pkg.quota_cache_ttl_seconds !== null ? String(pkg.quota_cache_ttl_seconds) : '300',
      
      // NEW: Handle allowed_models
      allowed_models: pkg.allowed_models ? JSON.stringify(pkg.allowed_models) : '[]',
    });
    
    // NEW: Set selected models from package data
    try {
      const modelsArray = pkg.allowed_models ? (Array.isArray(pkg.allowed_models) ? pkg.allowed_models : JSON.parse(pkg.allowed_models)) : [];
      setSelectedModels(modelsArray);
    } catch (error) {
      console.error("Error parsing allowed_models:", error);
      setSelectedModels([]);
    }
    
    setUpdatePackageError(null);
    setOpenEditDialog(true);
  };
  const handleCloseEditDialog = () => setOpenEditDialog(false);

  const handleOpenDeleteDialog = (pkg) => {
    setDeletingPackage(pkg);
    setDeletePackageError(null);
    setOpenDeleteDialog(true);
  };
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeletingPackage(null);
  };

  const handlePackageFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setPackageForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // NEW: Handle model selection
  const handleModelToggle = (modelIdentifier) => {
    setSelectedModels(prev => {
      const isSelected = prev.includes(modelIdentifier);
      const newSelection = isSelected 
        ? prev.filter(id => id !== modelIdentifier)
        : [...prev, modelIdentifier];
      
      // Update the form field as well
      setPackageForm(prevForm => ({
        ...prevForm,
        allowed_models: JSON.stringify(newSelection)
      }));
      
      return newSelection;
    });
  };

  const handleSelectAllModels = (checked) => {
    const newSelection = checked ? availableModels.map(model => model.model_identifier) : [];
    setSelectedModels(newSelection);
    setPackageForm(prev => ({
      ...prev,
      allowed_models: JSON.stringify(newSelection)
    }));
  };

  const preparePayload = (formState) => {
    const payload = {
      // Core Details
      name: formState.name,
      description: formState.description,
      price_usd: safeParseFloat(formState.price_usd),
      package_tier: formState.package_tier,
      is_active: formState.is_active,
      is_popular: formState.is_popular,
      is_enterprise: formState.is_enterprise,
      sort_order: safeParseInt(formState.sort_order),

      // Billing & Stripe
      stripe_price_id: formState.stripe_price_id,
      stripe_product_id: formState.stripe_product_id,
      billing_enabled: formState.billing_enabled,
      overage_allowed: formState.overage_allowed,
      price_per_extra_token: safeParseFloat(formState.price_per_extra_token),
      trial_period_days: safeParseInt(formState.trial_period_days),
      auto_upgrade_enabled: formState.auto_upgrade_enabled,

      // Token & Cost Limits
      limit_monthly_tokens: safeParseInt(formState.limit_monthly_tokens),
      daily_token_limit: safeParseInt(formState.daily_token_limit),
      per_request_token_limit: safeParseInt(formState.per_request_token_limit),
      limit_monthly_cost: safeParseFloat(formState.limit_monthly_cost),
      daily_cost_limit: safeParseFloat(formState.daily_cost_limit),
      per_request_cost_limit: safeParseFloat(formState.per_request_cost_limit),

      // Usage Limits
      limit_monthly_messages: safeParseInt(formState.limit_monthly_messages),
      limit_daily_messages: safeParseInt(formState.limit_daily_messages),
      hourly_message_limit: safeParseInt(formState.hourly_message_limit),
      limit_monthly_sessions: safeParseInt(formState.limit_monthly_sessions),
      limit_monthly_searches: safeParseInt(formState.limit_monthly_searches),
      limit_daily_searches: safeParseInt(formState.limit_daily_searches),
      limit_monthly_deep_searches: safeParseInt(formState.limit_monthly_deep_searches),
      limit_daily_deep_searches: safeParseInt(formState.limit_daily_deep_searches),
      concurrent_requests_limit: safeParseInt(formState.concurrent_requests_limit),
      limit_team_members: safeParseInt(formState.limit_team_members),
      limit_total_files: safeParseInt(formState.limit_total_files),

      // Rate Limiting
      requests_per_minute: safeParseInt(formState.requests_per_minute),
      requests_per_hour: safeParseInt(formState.requests_per_hour),
      burst_limit: safeParseInt(formState.burst_limit),
      max_active_sessions: safeParseInt(formState.max_active_sessions),
      max_conversation_length: safeParseInt(formState.max_conversation_length),

      // File & Upload Limits
      max_file_size_mb: safeParseInt(formState.max_file_size_mb),
      max_files_per_upload: safeParseInt(formState.max_files_per_upload),
      monthly_upload_limit_mb: safeParseInt(formState.monthly_upload_limit_mb),
      limit_total_upload_mb: formState.limit_total_upload_mb === '' ? null : Number(formState.limit_total_upload_mb),
      allowed_file_types: formState.allowed_file_types ? JSON.parse(formState.allowed_file_types) : null,
      allowed_models: formState.allowed_models ? JSON.parse(formState.allowed_models) : null, // NEW: Parse JSON array

      // Feature Flags
      api_access_enabled: formState.api_access_enabled,
      premium_models_enabled: formState.premium_models_enabled,
      custom_model_access: formState.custom_model_access,
      team_collaboration_enabled: formState.team_collaboration_enabled,
      chatwoot_integration_enabled: formState.chatwoot_integration_enabled,
      priority_support: formState.priority_support,
      webhook_enabled: formState.webhook_enabled,
      analytics_enabled: formState.analytics_enabled,
      export_data_enabled: formState.export_data_enabled,

      // Enterprise Features
      custom_branding_enabled: formState.custom_branding_enabled,
      white_label_enabled: formState.white_label_enabled,
      sso_enabled: formState.sso_enabled,
      audit_logs_enabled: formState.audit_logs_enabled,

      // Configuration
      features: formState.features ? JSON.parse(formState.features) : {},
      custom_config: formState.custom_config ? JSON.parse(formState.custom_config) : {},

      // Misc
      chat_history_retention_days: safeParseInt(formState.chat_history_retention_days),
      quota_cache_ttl_seconds: safeParseInt(formState.quota_cache_ttl_seconds),
    };
    
    // Basic validation
    if (!payload.name) throw new Error("Package Name is required.");
    if (payload.price_usd === null || isNaN(payload.price_usd)) throw new Error("Price (USD) is required and must be a number.");

    return payload;
  };

  const handleSubmitCreatePackage = async () => {
    setIsCreatingPackage(true);
    setCreatePackageError(null);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error("Authentication token not found.");
      const anonKey = (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_ANON_KEY) || process.env.REACT_APP_SUPABASE_ANON_KEY;
      if (!anonKey) throw new Error("Supabase anon key not configured.");
      const payload = preparePayload(packageForm);

      const response = await fetch(`/functions/v1/admin-package-manager/packages`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'apikey': anonKey, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || `Failed to create package: ${response.status} ${result.message}`);
      handleFetchPackages();
      handleCloseCreateDialog();
    } catch (error) {
      console.error("Error creating package:", error);
      setCreatePackageError(error.message);
    } finally {
      setIsCreatingPackage(false);
    }
  };
  
  const handleSubmitUpdatePackage = async () => {
    if (!editingPackage) return;
    setIsUpdatingPackage(true);
    setUpdatePackageError(null);
    console.log('[PackagesAdmin] Starting update for package ID:', editingPackage.id);
    
    try {
      const token = await getAuthToken();
      if (!token) throw new Error("Authentication token not found.");
      const anonKey = (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_ANON_KEY) || process.env.REACT_APP_SUPABASE_ANON_KEY;
      if (!anonKey) throw new Error("Supabase anon key not configured.");
      
      console.log('[PackagesAdmin] Preparing payload with form state:', packageForm);
      const payload = preparePayload(packageForm);
      console.log('[PackagesAdmin] Payload to be sent:', JSON.stringify(payload, null, 2));

      const response = await fetch(`/functions/v1/admin-package-manager/packages/${editingPackage.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'apikey': anonKey, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      console.log(`[PackagesAdmin] Received response from backend with status: ${response.status}`);
      const result = await response.json();
      console.log('[PackagesAdmin] Backend response body:', result);

      if (!response.ok) {
        throw new Error(result.error || `Failed to update package: ${response.status} ${result.message}`);
      }
      
      console.log('[PackagesAdmin] Update successful. Manually updating local state instead of re-fetching.');
      
      // Manually update the state to avoid race conditions with re-fetching
      setPackages(prevPackages => 
        prevPackages.map(p => p.id === editingPackage.id ? result.package : p)
      );

      handleCloseEditDialog();
    } catch (error) {
      console.error("Error updating package:", error);
      setUpdatePackageError(error.message);
    } finally {
      setIsUpdatingPackage(false);
    }
  };

  const handleSubmitDeletePackage = async () => {
    if (!deletingPackage) return;
    setIsDeletingPackage(true);
    setDeletePackageError(null);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error("Authentication token not found.");
      const anonKey = (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_ANON_KEY) || process.env.REACT_APP_SUPABASE_ANON_KEY;
      if (!anonKey) throw new Error("Supabase anon key not configured.");

      const response = await fetch(`/functions/v1/admin-package-manager/packages/${deletingPackage.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'apikey': anonKey, 'Content-Type': 'application/json' }
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || `Failed to delete package: ${response.status} ${result.message}`);
      handleFetchPackages();
      handleCloseDeleteDialog();
    } catch (error) {
      console.error("Error deleting package:", error);
      setDeletePackageError(error.message);
    } finally {
      setIsDeletingPackage(false);
    }
  };

  // Duplicate package handlers
  const handleOpenDuplicateDialog = (pkg) => {
    setDuplicatingPackage(pkg);
    setOpenDuplicateDialog(true);
  };

  const handleCloseDuplicateDialog = () => {
    setOpenDuplicateDialog(false);
    setDuplicatingPackage(null);
  };

  const handleDuplicatePackage = () => {
    if (!duplicatingPackage) return;
    
    // Simple copy - just duplicate the package with "- Copy" suffix
    // User will manually change name to "Annual" and adjust price
    const duplicatedData = {
      // Core Details - Just copy, add "- Copy" to name
      name: `${duplicatingPackage.name} - Copy`,
      description: duplicatingPackage.description || '',
      price_usd: duplicatingPackage.price_usd !== null ? String(duplicatingPackage.price_usd) : '',
      package_tier: duplicatingPackage.package_tier || 'basic',
      is_active: false, // Start as inactive
      is_popular: false,
      is_enterprise: duplicatingPackage.is_enterprise || false,
      sort_order: (duplicatingPackage.sort_order || 0) + 1,

      // Discount Settings - Copy or default to 0
      discount_percentage: duplicatingPackage.discount_percentage !== null ? String(duplicatingPackage.discount_percentage) : '0',
      discount_active: duplicatingPackage.discount_active || false,
      discount_reason: duplicatingPackage.discount_reason || '',

      // Billing & Stripe - CLEAR stripe_price_id
      stripe_price_id: '', // User must add new Stripe Price ID
      stripe_product_id: duplicatingPackage.stripe_product_id || '',
      billing_enabled: duplicatingPackage.billing_enabled !== undefined ? duplicatingPackage.billing_enabled : true,
      overage_allowed: duplicatingPackage.overage_allowed || false,
      price_per_extra_token: duplicatingPackage.price_per_extra_token !== null ? String(duplicatingPackage.price_per_extra_token) : '',
      trial_period_days: duplicatingPackage.trial_period_days !== null ? String(duplicatingPackage.trial_period_days) : '0',
      auto_upgrade_enabled: duplicatingPackage.auto_upgrade_enabled || false,

      // Token & Cost Limits - EXACT COPY (no multiplication)
      limit_monthly_tokens: duplicatingPackage.limit_monthly_tokens !== null ? String(duplicatingPackage.limit_monthly_tokens) : '',
      daily_token_limit: duplicatingPackage.daily_token_limit !== null ? String(duplicatingPackage.daily_token_limit) : '',
      per_request_token_limit: duplicatingPackage.per_request_token_limit !== null ? String(duplicatingPackage.per_request_token_limit) : '',
      limit_monthly_cost: duplicatingPackage.limit_monthly_cost !== null ? String(duplicatingPackage.limit_monthly_cost) : '',
      daily_cost_limit: duplicatingPackage.daily_cost_limit !== null ? String(duplicatingPackage.daily_cost_limit) : '',
      per_request_cost_limit: duplicatingPackage.per_request_cost_limit !== null ? String(duplicatingPackage.per_request_cost_limit) : '',
      
      // Usage Limits - EXACT COPY
      limit_monthly_messages: duplicatingPackage.limit_monthly_messages !== null ? String(duplicatingPackage.limit_monthly_messages) : '',
      limit_daily_messages: duplicatingPackage.limit_daily_messages !== null ? String(duplicatingPackage.limit_daily_messages) : '',
      hourly_message_limit: duplicatingPackage.hourly_message_limit !== null ? String(duplicatingPackage.hourly_message_limit) : '',
      limit_monthly_sessions: duplicatingPackage.limit_monthly_sessions !== null ? String(duplicatingPackage.limit_monthly_sessions) : '',
      limit_monthly_searches: duplicatingPackage.limit_monthly_searches !== null ? String(duplicatingPackage.limit_monthly_searches) : '',
      limit_daily_searches: duplicatingPackage.limit_daily_searches !== null ? String(duplicatingPackage.limit_daily_searches) : '',
      limit_monthly_deep_searches: duplicatingPackage.limit_monthly_deep_searches !== null ? String(duplicatingPackage.limit_monthly_deep_searches) : '',
      limit_daily_deep_searches: duplicatingPackage.limit_daily_deep_searches !== null ? String(duplicatingPackage.limit_daily_deep_searches) : '',
      concurrent_requests_limit: duplicatingPackage.concurrent_requests_limit !== null ? String(duplicatingPackage.concurrent_requests_limit) : '',
      limit_team_members: duplicatingPackage.limit_team_members !== null ? String(duplicatingPackage.limit_team_members) : '',
      limit_total_files: duplicatingPackage.limit_total_files !== null ? String(duplicatingPackage.limit_total_files) : '',
      
      // Rate Limiting - EXACT COPY
      requests_per_minute: duplicatingPackage.requests_per_minute !== null ? String(duplicatingPackage.requests_per_minute) : '',
      requests_per_hour: duplicatingPackage.requests_per_hour !== null ? String(duplicatingPackage.requests_per_hour) : '',
      burst_limit: duplicatingPackage.burst_limit !== null ? String(duplicatingPackage.burst_limit) : '',
      max_active_sessions: duplicatingPackage.max_active_sessions !== null ? String(duplicatingPackage.max_active_sessions) : '',
      max_conversation_length: duplicatingPackage.max_conversation_length !== null ? String(duplicatingPackage.max_conversation_length) : '',
      
      // File & Upload Limits - EXACT COPY
      max_file_size_mb: duplicatingPackage.max_file_size_mb !== null ? String(duplicatingPackage.max_file_size_mb) : '',
      max_files_per_upload: duplicatingPackage.max_files_per_upload !== null ? String(duplicatingPackage.max_files_per_upload) : '',
      monthly_upload_limit_mb: duplicatingPackage.monthly_upload_limit_mb !== null ? String(duplicatingPackage.monthly_upload_limit_mb) : '',
      limit_total_upload_mb: duplicatingPackage.limit_total_upload_mb !== null ? String(duplicatingPackage.limit_total_upload_mb) : '',
      allowed_file_types: duplicatingPackage.allowed_file_types ? JSON.stringify(duplicatingPackage.allowed_file_types) : '["pdf", "txt", "docx", "md"]',
      allowed_models: duplicatingPackage.allowed_models ? JSON.stringify(duplicatingPackage.allowed_models) : '[]',
      
      // Feature Flags - EXACT COPY
      api_access_enabled: duplicatingPackage.api_access_enabled || false,
      premium_models_enabled: duplicatingPackage.premium_models_enabled || false,
      custom_model_access: duplicatingPackage.custom_model_access || false,
      team_collaboration_enabled: duplicatingPackage.team_collaboration_enabled || false,
      chatwoot_integration_enabled: duplicatingPackage.chatwoot_integration_enabled || false,
      priority_support: duplicatingPackage.priority_support || false,
      webhook_enabled: duplicatingPackage.webhook_enabled || false,
      analytics_enabled: duplicatingPackage.analytics_enabled !== undefined ? duplicatingPackage.analytics_enabled : true,
      export_data_enabled: duplicatingPackage.export_data_enabled || false,
      
      // Enterprise Features - EXACT COPY
      custom_branding_enabled: duplicatingPackage.custom_branding_enabled || false,
      white_label_enabled: duplicatingPackage.white_label_enabled || false,
      sso_enabled: duplicatingPackage.sso_enabled || false,
      audit_logs_enabled: duplicatingPackage.audit_logs_enabled || false,
      
      // Configuration - EXACT COPY
      features: duplicatingPackage.features ? JSON.stringify(duplicatingPackage.features) : '{}',
      custom_config: duplicatingPackage.custom_config ? JSON.stringify(duplicatingPackage.custom_config) : '{}',
      
      // Misc - EXACT COPY
      chat_history_retention_days: duplicatingPackage.chat_history_retention_days !== null ? String(duplicatingPackage.chat_history_retention_days) : '30',
      quota_cache_ttl_seconds: duplicatingPackage.quota_cache_ttl_seconds !== null ? String(duplicatingPackage.quota_cache_ttl_seconds) : '300',
    };
    
    // Set form and selected models, then open create dialog
    setPackageForm(duplicatedData);
    
    // Set selected models from package data
    try {
      const modelsArray = duplicatingPackage.allowed_models 
        ? (Array.isArray(duplicatingPackage.allowed_models) 
            ? duplicatingPackage.allowed_models 
            : JSON.parse(duplicatingPackage.allowed_models)) 
        : [];
      setSelectedModels(modelsArray);
    } catch (error) {
      console.error("Error parsing allowed_models during duplicate:", error);
      setSelectedModels([]);
    }
    
    handleCloseDuplicateDialog();
    setOpenCreateDialog(true);
  };

  // Placeholder for handleToggleActive (will call update function)
  // const handleTogglePackageActive = async (pkg) => { ... };

  return (
    <Container>
      <Box sx={{ my: 4 }}>
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Grid item>
            <Typography variant="h4" gutterBottom>
              Manage Subscription Packages
            </Typography>
          </Grid>
          <Grid item>
            <Button variant="contained" color="primary" onClick={handleOpenCreateDialog}>
              Create New Package
            </Button>
          </Grid>
        </Grid>
        
        <Button component={RouterLink} to="/admin" variant="outlined" sx={{ mb: 2 }}>
          Back to Admin Panel
        </Button>

        {loadingPackages && <CircularProgress sx={{ display: 'block', margin: 'auto', my: 2 }} />}
        {packagesError && (
          <Alert severity="error" sx={{ mt: 2 }}>{`Error fetching packages: ${packagesError}`}</Alert>
        )}
        {!loadingPackages && !packagesError && packages.length === 0 && (
          <Typography sx={{mt: 2, textAlign: 'center' }}>No packages found. Create one to get started!</Typography>
        )}

        {packages.length > 0 && (
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Price (USD)</TableCell>
                  <TableCell>Monthly Tokens</TableCell>
                  <TableCell>Monthly Messages</TableCell>
                  <TableCell>Stripe Price ID</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {packages.map((pkg) => (
                  <TableRow key={pkg.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell component="th" scope="row">{pkg.name}</TableCell>
                    <TableCell>${pkg.price_usd !== null ? pkg.price_usd.toFixed(2) : 'N/A'}</TableCell>
                    <TableCell>{pkg.limit_monthly_tokens ? pkg.limit_monthly_tokens.toLocaleString() : 'N/A'}</TableCell>
                    <TableCell>{pkg.limit_monthly_messages ? pkg.limit_monthly_messages.toLocaleString() : 'N/A'}</TableCell>
                    <TableCell>{pkg.stripe_price_id || 'Not Set'}</TableCell>
                    <TableCell align="center">
                      <Chip 
                        icon={pkg.is_active ? <ToggleOnIcon /> : <ToggleOffIcon />} 
                        label={pkg.is_active ? 'Active' : 'Inactive'} 
                        color={pkg.is_active ? 'success' : 'default'} 
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleOpenEditDialog(pkg)} title="Edit Package">
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleOpenDuplicateDialog(pkg)} 
                        title="Duplicate Package (for yearly/multi-month)"
                        color="primary"
                      >
                        <ContentCopyIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleOpenDeleteDialog(pkg)} 
                        title="Delete Package"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Create/Edit Package Dialog (unified for now, can be split later) */}
      <Dialog open={openCreateDialog || openEditDialog} onClose={editingPackage ? handleCloseEditDialog : handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPackage ? 'Edit Package' : 'Create New Package'}</DialogTitle>
        <DialogContent>
          {(createPackageError || updatePackageError) && 
            <Alert severity="error" sx={{ mb: 2 }}>{createPackageError || updatePackageError}</Alert>}
          
          <Typography variant="subtitle2" sx={{mt:1}}>Core Details</Typography>
          <TextField autoFocus margin="dense" name="name" label="Package Name" type="text" fullWidth variant="outlined" value={packageForm.name} onChange={handlePackageFormChange} />
          <TextField margin="dense" name="price_usd" label="Price (USD)" type="number" fullWidth variant="outlined" value={packageForm.price_usd} onChange={handlePackageFormChange} InputProps={{ inputProps: { step: "0.01" } }} />
          
          {/* NEW: Discount Section */}
          <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>💰 Discount Settings (Optional)</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField 
                  name="discount_percentage" 
                  label="Discount %" 
                  type="number" 
                  fullWidth 
                  variant="outlined" 
                  value={packageForm.discount_percentage || ''} 
                  onChange={handlePackageFormChange} 
                  InputProps={{ inputProps: { min: 0, max: 100, step: "0.01" } }}
                  helperText="e.g., 10 for 10% off" 
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  name="discount_reason" 
                  label="Discount Reason" 
                  type="text" 
                  fullWidth 
                  variant="outlined" 
                  value={packageForm.discount_reason || ''} 
                  onChange={handlePackageFormChange}
                  helperText="e.g., annual-plan, promotional, early-bird" 
                />
              </Grid>
              {packageForm.price_usd && packageForm.discount_percentage && (
                <Grid item xs={12}>
                  <Box sx={{ p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #4caf50' }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Original Price:</strong> ${parseFloat(packageForm.price_usd).toFixed(2)}/month
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Discount:</strong> {parseFloat(packageForm.discount_percentage || 0).toFixed(2)}%
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                      <strong>Final Price:</strong> ${(parseFloat(packageForm.price_usd) * (1 - parseFloat(packageForm.discount_percentage || 0) / 100)).toFixed(2)}/month
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
          
          <TextField margin="dense" name="description" label="Description (optional)" type="text" fullWidth multiline rows={3} variant="outlined" value={packageForm.description} onChange={handlePackageFormChange} />
          <TextField margin="dense" name="package_tier" label="Package Tier" type="text" fullWidth variant="outlined" value={packageForm.package_tier} onChange={handlePackageFormChange} helperText="e.g., free, basic, pro, enterprise" />
          
          <Typography variant="subtitle2" sx={{mt:2}}>Billing & Stripe</Typography>
          <TextField margin="dense" name="stripe_price_id" label="Stripe Price ID" type="text" fullWidth variant="outlined" value={packageForm.stripe_price_id} onChange={handlePackageFormChange} helperText="e.g., price_123abc..." />
          <TextField margin="dense" name="stripe_product_id" label="Stripe Product ID" type="text" fullWidth variant="outlined" value={packageForm.stripe_product_id} onChange={handlePackageFormChange} helperText="e.g., prod_123abc..." />
          <TextField margin="dense" name="price_per_extra_token" label="Price Per Extra Token" type="number" fullWidth variant="outlined" value={packageForm.price_per_extra_token} onChange={handlePackageFormChange} InputProps={{ inputProps: { step: "0.0001" } }} helperText="Cost for each token over the limit" />
          <TextField margin="dense" name="trial_period_days" label="Trial Period (Days)" type="number" fullWidth variant="outlined" value={packageForm.trial_period_days} onChange={handlePackageFormChange} />
          <TextField margin="dense" name="sort_order" label="Sort Order" type="number" fullWidth variant="outlined" value={packageForm.sort_order} onChange={handlePackageFormChange} helperText="Lower numbers appear first" />
          <FormControlLabel control={<Switch checked={packageForm.auto_upgrade_enabled} onChange={handlePackageFormChange} name="auto_upgrade_enabled" />} label="Auto Upgrade Enabled" sx={{ mt: 1 }} />

          <Typography variant="subtitle2" sx={{mt: 2}}>Token & Cost Limits</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField name="limit_monthly_tokens" label="Monthly Token Limit" type="number" fullWidth variant="outlined" value={packageForm.limit_monthly_tokens} onChange={handlePackageFormChange} /></Grid>
            <Grid item xs={6}><TextField name="limit_monthly_cost" label="Monthly Cost Limit (USD)" type="number" fullWidth variant="outlined" value={packageForm.limit_monthly_cost} onChange={handlePackageFormChange} /></Grid>
            <Grid item xs={6}><TextField name="daily_token_limit" label="Daily Token Limit" type="number" fullWidth variant="outlined" value={packageForm.daily_token_limit} onChange={handlePackageFormChange} /></Grid>
            <Grid item xs={6}><TextField name="daily_cost_limit" label="Daily Cost Limit (USD)" type="number" fullWidth variant="outlined" value={packageForm.daily_cost_limit} onChange={handlePackageFormChange} /></Grid>
            <Grid item xs={6}><TextField name="per_request_token_limit" label="Per-Request Token Limit" type="number" fullWidth variant="outlined" value={packageForm.per_request_token_limit} onChange={handlePackageFormChange} /></Grid>
            <Grid item xs={6}><TextField name="per_request_cost_limit" label="Per-Request Cost Limit (USD)" type="number" fullWidth variant="outlined" value={packageForm.per_request_cost_limit} onChange={handlePackageFormChange} /></Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{mt: 2}}>Usage Limits</Typography>
           <Grid container spacing={2}>
            <Grid item xs={6}><TextField name="limit_monthly_messages" label="Monthly Message Limit" type="number" fullWidth variant="outlined" value={packageForm.limit_monthly_messages} onChange={handlePackageFormChange} /></Grid>
            <Grid item xs={6}><TextField name="limit_daily_messages" label="Daily Message Limit" type="number" fullWidth variant="outlined" value={packageForm.limit_daily_messages} onChange={handlePackageFormChange} /></Grid>
            <Grid item xs={6}><TextField name="hourly_message_limit" label="Hourly Message Limit" type="number" fullWidth variant="outlined" value={packageForm.hourly_message_limit} onChange={handlePackageFormChange} /></Grid>
            <Grid item xs={6}><TextField name="limit_team_members" label="Team Member Limit" type="number" fullWidth variant="outlined" value={packageForm.limit_team_members} onChange={handlePackageFormChange} /></Grid>
            <Grid item xs={6}><TextField name="limit_total_files" label="Total Files Limit (Company)" type="number" fullWidth variant="outlined" value={packageForm.limit_total_files} onChange={handlePackageFormChange} helperText="-1 or empty = unlimited" /></Grid>
            <Grid item xs={6}><TextField name="limit_monthly_sessions" label="Monthly Session Limit" type="number" fullWidth variant="outlined" value={packageForm.limit_monthly_sessions} onChange={handlePackageFormChange} /></Grid>
            <Grid item xs={6}><TextField name="concurrent_requests_limit" label="Concurrent Requests" type="number" fullWidth variant="outlined" value={packageForm.concurrent_requests_limit} onChange={handlePackageFormChange} /></Grid>
            <Grid item xs={6}><TextField name="limit_monthly_searches" label="Monthly Search Limit" type="number" fullWidth variant="outlined" value={packageForm.limit_monthly_searches} onChange={handlePackageFormChange} /></Grid>
            <Grid item xs={6}><TextField name="limit_daily_searches" label="Daily Search Limit" type="number" fullWidth variant="outlined" value={packageForm.limit_daily_searches} onChange={handlePackageFormChange} /></Grid>
            <Grid item xs={6}><TextField name="limit_monthly_deep_searches" label="Monthly Deep Search Limit" type="number" fullWidth variant="outlined" value={packageForm.limit_monthly_deep_searches} onChange={handlePackageFormChange} /></Grid>
            <Grid item xs={6}><TextField name="limit_daily_deep_searches" label="Daily Deep Search Limit" type="number" fullWidth variant="outlined" value={packageForm.limit_daily_deep_searches} onChange={handlePackageFormChange} /></Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{mt: 2}}>Rate Limiting & Sessions</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField name="requests_per_minute" label="Requests Per Minute" type="number" fullWidth variant="outlined" value={packageForm.requests_per_minute} onChange={handlePackageFormChange} /></Grid>
            <Grid item xs={6}><TextField name="requests_per_hour" label="Requests Per Hour" type="number" fullWidth variant="outlined" value={packageForm.requests_per_hour} onChange={handlePackageFormChange} /></Grid>
            <Grid item xs={6}><TextField name="burst_limit" label="Burst Limit" type="number" fullWidth variant="outlined" value={packageForm.burst_limit} onChange={handlePackageFormChange} /></Grid>
            <Grid item xs={6}><TextField name="max_active_sessions" label="Max Active Sessions" type="number" fullWidth variant="outlined" value={packageForm.max_active_sessions} onChange={handlePackageFormChange} /></Grid>
            <Grid item xs={6}><TextField name="max_conversation_length" label="Max Conversation Length" type="number" fullWidth variant="outlined" value={packageForm.max_conversation_length} onChange={handlePackageFormChange} /></Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{mt: 2}}>File & Upload Limits</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField name="max_file_size_mb" label="Max File Size (MB)" type="number" fullWidth variant="outlined" value={packageForm.max_file_size_mb} onChange={handlePackageFormChange} /></Grid>
            <Grid item xs={6}><TextField name="max_files_per_upload" label="Max Files Per Upload" type="number" fullWidth variant="outlined" value={packageForm.max_files_per_upload} onChange={handlePackageFormChange} /></Grid>
            <Grid item xs={6}><TextField name="monthly_upload_limit_mb" label="Monthly Upload Limit (MB)" type="number" fullWidth variant="outlined" value={packageForm.monthly_upload_limit_mb} onChange={handlePackageFormChange} /></Grid>
            <Grid item xs={12}><TextField name="allowed_file_types" label="Allowed File Types (JSON)" fullWidth variant="outlined" value={packageForm.allowed_file_types} onChange={handlePackageFormChange} helperText='JSON array, e.g., ["pdf", "txt", "docx", "md"]' /></Grid>
          </Grid>

          {/* NEW: LLM Model Selection Section */}
          <Typography variant="subtitle2" sx={{mt: 2}}>Allowed LLM Models</Typography>
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mt: 1 }}>
            {loadingModels ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
                <Typography sx={{ ml: 1 }}>Loading models...</Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedModels.length === availableModels.length && availableModels.length > 0}
                        indeterminate={selectedModels.length > 0 && selectedModels.length < availableModels.length}
                        onChange={(e) => handleSelectAllModels(e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2" sx={{ fontWeight: 'bold' }}>Select All Models</Typography>}
                  />
                </Box>
                <Divider sx={{ mb: 2 }} />
                <FormGroup>
                  {availableModels.map((model) => (
                    <FormControlLabel
                      key={model.model_identifier}
                      control={
                        <Checkbox
                          checked={selectedModels.includes(model.model_identifier)}
                          onChange={() => handleModelToggle(model.model_identifier)}
                          size="small"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">
                            {model.display_label}
                            {model.is_premium && (
                              <Chip 
                                label="Premium" 
                                size="small" 
                                color="primary" 
                                sx={{ ml: 1, fontSize: '0.7rem', height: '18px' }} 
                              />
                            )}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Provider: {model.provider} • ID: {model.model_identifier}
                          </Typography>
                        </Box>
                      }
                      sx={{ mb: 0.5 }}
                    />
                  ))}
                  {availableModels.length === 0 && (
                    <Typography variant="body2" color="textSecondary" sx={{ p: 1 }}>
                      No active models found. Check your LLM models configuration.
                    </Typography>
                  )}
                </FormGroup>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  {selectedModels.length} model(s) selected. Leave empty to allow all models.
                </Typography>
              </>
            )}
          </Box>

          <Typography variant="subtitle2" sx={{mt: 2}}>Feature Flags</Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={4}>
              <TextField name="limit_total_upload_mb" label="Total Upload Limit (MB)" type="number" fullWidth value={packageForm.limit_total_upload_mb} onChange={handlePackageFormChange} />
            </Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={packageForm.billing_enabled} onChange={handlePackageFormChange} name="billing_enabled" />} label="Billing Enabled" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={packageForm.overage_allowed} onChange={handlePackageFormChange} name="overage_allowed" />} label="Overage Allowed" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={packageForm.api_access_enabled} onChange={handlePackageFormChange} name="api_access_enabled" />} label="API Access" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={packageForm.premium_models_enabled} onChange={handlePackageFormChange} name="premium_models_enabled" />} label="Premium Models" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={packageForm.custom_model_access} onChange={handlePackageFormChange} name="custom_model_access" />} label="Custom Model Access" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={packageForm.team_collaboration_enabled} onChange={handlePackageFormChange} name="team_collaboration_enabled" />} label="Team Collaboration" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={packageForm.chatwoot_integration_enabled} onChange={handlePackageFormChange} name="chatwoot_integration_enabled" />} label="Chatwoot Integration" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={packageForm.priority_support} onChange={handlePackageFormChange} name="priority_support" />} label="Priority Support" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={packageForm.webhook_enabled} onChange={handlePackageFormChange} name="webhook_enabled" />} label="Webhook Enabled" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={packageForm.analytics_enabled} onChange={handlePackageFormChange} name="analytics_enabled" />} label="Analytics Enabled" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={packageForm.export_data_enabled} onChange={handlePackageFormChange} name="export_data_enabled" />} label="Export Data Enabled" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={packageForm.is_popular} onChange={handlePackageFormChange} name="is_popular" />} label="Is Popular" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={packageForm.is_enterprise} onChange={handlePackageFormChange} name="is_enterprise" />} label="Is Enterprise" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={packageForm.allow_manual_session_deletion || false} onChange={handlePackageFormChange} name="allow_manual_session_deletion" />} label="Allow Manual Session Deletion" /></Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{mt: 2}}>Enterprise Features</Typography>
          <Grid container spacing={1}>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={packageForm.custom_branding_enabled} onChange={handlePackageFormChange} name="custom_branding_enabled" />} label="Custom Branding" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={packageForm.white_label_enabled} onChange={handlePackageFormChange} name="white_label_enabled" />} label="White Label" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={packageForm.sso_enabled} onChange={handlePackageFormChange} name="sso_enabled" />} label="SSO Enabled" /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={<Switch checked={packageForm.audit_logs_enabled} onChange={handlePackageFormChange} name="audit_logs_enabled" />} label="Audit Logs" /></Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{mt: 2}}>Advanced Configuration</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}><TextField name="features" label="Features (JSON)" fullWidth multiline rows={3} variant="outlined" value={packageForm.features} onChange={handlePackageFormChange} helperText='JSON object for additional features, e.g., {"feature1": true}' /></Grid>
            <Grid item xs={12}><TextField name="custom_config" label="Custom Config (JSON)" fullWidth multiline rows={3} variant="outlined" value={packageForm.custom_config} onChange={handlePackageFormChange} helperText='JSON object for custom configuration' /></Grid>
          </Grid>
          
          <Typography variant="subtitle2" sx={{mt: 2}}>Miscellaneous</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField name="chat_history_retention_days" label="Chat History (Days)" type="number" fullWidth variant="outlined" value={packageForm.chat_history_retention_days} onChange={handlePackageFormChange} /></Grid>
            <Grid item xs={6}><TextField name="quota_cache_ttl_seconds" label="Quota Cache TTL (Seconds)" type="number" fullWidth variant="outlined" value={packageForm.quota_cache_ttl_seconds} onChange={handlePackageFormChange} /></Grid>
          </Grid>

          <FormControlLabel control={<Switch checked={packageForm.is_active} onChange={handlePackageFormChange} name="is_active" />} label="Package is Active" sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px'}}>
          <Button onClick={editingPackage ? handleCloseEditDialog : handleCloseCreateDialog} color="secondary">Cancel</Button>
          <Button 
            onClick={editingPackage ? handleSubmitUpdatePackage : handleSubmitCreatePackage}
            variant="contained" 
            color="primary" 
            disabled={isCreatingPackage || isUpdatingPackage}
          >
            {(isCreatingPackage || isUpdatingPackage) ? <CircularProgress size={24} /> : (editingPackage ? 'Save Changes' : 'Create Package')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Package Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Package</DialogTitle>
        <DialogContent>
          {deletePackageError && 
            <Alert severity="error" sx={{ mb: 2 }}>{deletePackageError}</Alert>}
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete the package "{deletingPackage?.name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone. Users with active subscriptions to this package may be affected.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px'}}>
          <Button onClick={handleCloseDeleteDialog} color="secondary">Cancel</Button>
          <Button 
            onClick={handleSubmitDeletePackage}
            variant="contained" 
            color="error" 
            disabled={isDeletingPackage}
          >
            {isDeletingPackage ? <CircularProgress size={24} /> : 'Delete Package'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Duplicate Package Dialog */}
      <Dialog open={openDuplicateDialog} onClose={handleCloseDuplicateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Duplicate Package</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Create a copy of "{duplicatingPackage?.name}"
          </Typography>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            This will create a new package with:
            <ul>
              <li>✅ All limits copied exactly (same monthly/daily limits)</li>
              <li>✅ Name changed to "{duplicatingPackage?.name} - Copy"</li>
              <li>⚠️ Stripe Price ID cleared (you must add new one)</li>
              <li>⚠️ Starts as INACTIVE</li>
            </ul>
          </Alert>
          
          <Typography variant="body2" color="text.secondary">
            Common use case: Create annual version with same limits but discounted price
          </Typography>
          
          {duplicatingPackage && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Current Package:</Typography>
              <Typography variant="body2">Name: {duplicatingPackage.name}</Typography>
              <Typography variant="body2">Price: ${duplicatingPackage.price_usd}/month</Typography>
              <Typography variant="body2">Monthly Tokens: {(duplicatingPackage.limit_monthly_tokens || 0).toLocaleString()}</Typography>
              <Typography variant="body2">Monthly Messages: {(duplicatingPackage.limit_monthly_messages || 0).toLocaleString()}</Typography>
            </Box>
          )}
          
          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>After duplicating:</strong><br/>
            1. Change name to "Annual" or your desired name<br/>
            2. Adjust price (e.g., apply discount for annual)<br/>
            3. Add new Stripe Price ID<br/>
            4. Keep all limits the same (industry standard)
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDuplicateDialog}>Cancel</Button>
          <Button 
            onClick={handleDuplicatePackage}
            variant="contained" 
            color="primary"
          >
            Duplicate Package
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default PackagesAdmin; 